import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { IconEdit } from "@tabler/icons-react";
import type { JSX } from "react";
import { useMutation } from "urql";
import ActionButton from "../../ActionButton";
import type { ItemRole, ItemType } from "../../utils";
import ItemForm, { type IFormValues } from "./ItemForm";
import {
    type TuneThreadUpdateItemMutation,
    type TuneThreadUpdateItemMutationVariables,
    updateItemMutation,
} from "./queries";

interface IProps {
    tuneThreadItem: {
        id: number;
        type: ItemType;
        role: ItemRole | null; // Role can be null for function calls
        content: string;
        context: boolean;
        toolCallId: string | null;
        toolName: string | null;
        toolArguments: string | null;
        imageB64?: string | null;
        imageName?: string | null;
    };
}

function EditItemButton({ tuneThreadItem }: IProps): JSX.Element {
    const [updateResult, executeUpdate] = useMutation<
        TuneThreadUpdateItemMutation,
        TuneThreadUpdateItemMutationVariables
    >(updateItemMutation);

    const edit = async (values: IFormValues) => {
        const isMessage = values.type === "MESSAGE";
        const isFunctionCall = values.type === "FUNCTION_CALL";
        const isFunctionOutput = values.type === "FUNCTION_CALL_OUTPUT";

        const result = await executeUpdate({
            data: {
                tuneThreadItemId: tuneThreadItem.id,
                item: {
                    type: values.type,
                    role: isMessage ? values.role : null,
                    content: values.content,
                    imageB64: isMessage ? values.imageB64 : null,
                    imageName: isMessage ? values.imageName || null : null,
                    context: tuneThreadItem.context,
                    toolCallId: isFunctionCall || isFunctionOutput ? values.toolCallId || null : null,
                    toolName: isFunctionCall ? values.toolName || null : null,
                    toolArguments:
                        isFunctionCall && Object.keys(values.toolArguments).length > 0
                            ? JSON.stringify(values.toolArguments)
                            : null,
                    reasoningSummary: [],
                    encryptedReasoning: null,
                    reasoningId: null,
                },
            },
        });
        if (result.error) {
            notifications.show({
                title: "Failed to update item",
                message: result.error.message,
            });
        } else {
            notifications.show({
                title: "Item updated",
                message: "Item updated",
            });
        }
    };

    if (tuneThreadItem.type === "REASONING") {
        return <ActionButton onClick={() => {}} label="Edit item" icon={<IconEdit stroke={1} />} disabled />;
    }

    const initialValues: IFormValues = {
        type: tuneThreadItem.type as Exclude<ItemType, "REASONING">,
        role: tuneThreadItem.role || "USER",
        content: tuneThreadItem.content,
        toolCallId: tuneThreadItem.toolCallId || "",
        toolName: (tuneThreadItem.toolName || "") as "documentSearch" | "readDocument" | "",
        toolArguments: tuneThreadItem.toolArguments ? JSON.parse(tuneThreadItem.toolArguments) : {},
        imageB64: tuneThreadItem.imageB64 || null,
        imageName: tuneThreadItem.imageName || "",
    };

    const onClick = () =>
        modals.open({
            title: "Edit item",
            size: "xl",
            children: (
                <ItemForm
                    fetching={updateResult.fetching}
                    handleSubmit={(values) => {
                        edit(values);
                        modals.closeAll();
                    }}
                    initialValues={initialValues}
                />
            ),
        });

    return (
        <ActionButton
            onClick={onClick}
            label="Edit item"
            icon={<IconEdit stroke={1} />}
            disabled={updateResult.fetching}
        />
    );
}

export default EditItemButton;
